import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getStoredRoles, getStoredAccesses, clearUserAuthData } from '../utils/roleAccess';
import { getMe } from '../services/auth.service';

const AuthContext = createContext();

function getStoredUser() {
  try {
    const raw = localStorage.getItem('userProfile');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getUserDisplayName(user) {
  if (!user) return 'Usuario';
  if (user.person?.name) {
    return `${user.person.name} ${user.person.lastName || ''}`.trim();
  }
  return user.username || 'Usuario';
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem('isAuthenticated') === 'true',
  );
  const [roles, setRoles] = useState(getStoredRoles());
  const [accesses, setAccesses] = useState(getStoredAccesses());
  const [user, setUser] = useState(getStoredUser());

  const fetchCurrentUser = useCallback(async () => {
    if (!localStorage.getItem('token')) return null;

    const storedRoles = getStoredRoles();
    const storedAccesses = getStoredAccesses();
    const storedUser = getStoredUser();

    if ((storedRoles.length > 0 || storedAccesses.length > 0) && storedUser) {
      setUser(storedUser);
      return storedUser;
    }

    try {
      const response = await getMe();
      const profile = response?.payload || null;
      if (profile) {
        setUser(profile);
        localStorage.setItem('userProfile', JSON.stringify(profile));
        if (profile.roles) {
          localStorage.setItem('userRoles', JSON.stringify(profile.roles));
          setRoles(profile.roles);
        }
        if (profile.accesses) {
          localStorage.setItem('userAccesses', JSON.stringify(profile.accesses));
          setAccesses(profile.accesses);
        }
      }
      return profile;
    } catch (error) {
      if (error?.response?.status === 404) {
        return storedUser;
      }
      return null;
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setRoles(getStoredRoles());
      setAccesses(getStoredAccesses());
      fetchCurrentUser();
    }
  }, [isAuthenticated, fetchCurrentUser]);

  const login = (authData = {}) => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');

    if (authData.roles) {
      localStorage.setItem('userRoles', JSON.stringify(authData.roles));
      setRoles(authData.roles);
    }
    if (authData.accesses) {
      localStorage.setItem('userAccesses', JSON.stringify(authData.accesses));
      setAccesses(authData.accesses);
    }

    const partialUser = {
      userId: authData.userId,
      username: authData.username,
      roles: authData.roles || [],
      accesses: authData.accesses || [],
      person: authData.person || null,
    };

    if (authData.userId || authData.username) {
      const profile = { ...partialUser };
      setUser((prev) => ({ ...prev, ...profile }));
      localStorage.setItem('userProfile', JSON.stringify(profile));
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setRoles([]);
    setAccesses([]);
    setUser(null);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userProfile');
    clearUserAuthData();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        roles,
        accesses,
        user,
        fetchCurrentUser,
        displayName: getUserDisplayName(user),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
